import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Save,
  Loader2,
  User as UserIcon,
  Bell,
  AlertTriangle,
  Hash,
  MessageCircle,
  Mail,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { useTranslation } from "@/hooks/useTranslation";

const INPUT_CLASS =
  "w-full bg-neutral-50 border border-neutral-200 rounded-control px-3 py-2.5 text-sm text-neutral-800 outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100";

// Job title is personal (your role). Labels reuse the onboarding.* keys.
const JOB_TITLES = [
  { value: "Media Buyer", key: "onboarding.companyRole.jobTitles.mediaBuyer" },
  { value: "Performance Manager", key: "onboarding.companyRole.jobTitles.performanceManager" },
  { value: "Marketing Director", key: "onboarding.companyRole.jobTitles.marketingDirector" },
  { value: "Account Manager", key: "onboarding.companyRole.jobTitles.accountManager" },
  { value: "Founder / CEO", key: "onboarding.companyRole.jobTitles.founder" },
];

const CHANNELS = [
  { id: "slack", labelKey: "onboarding.notifications.channels.slack", Icon: Hash },
  { id: "whatsapp", labelKey: "onboarding.notifications.channels.whatsapp", Icon: MessageCircle },
  { id: "email", labelKey: "onboarding.notifications.channels.email", Icon: Mail },
];

const FREQUENCIES = [
  { value: "Real-time", key: "onboarding.notifications.frequencies.realtime" },
  { value: "Hourly digest", key: "onboarding.notifications.frequencies.hourly" },
  { value: "Daily digest", key: "onboarding.notifications.frequencies.daily" },
];

type TabId = "account" | "notifications" | "danger";

export default function SettingsPage() {
  const { user, updateUser, signOut } = useAuth();
  const { settings, updateProfile, saving, deleteAccount, deleting } = useSettings();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tab, setTab] = useState<TabId>("account");

  // Account (personal identity)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  // Notification preferences
  const [notificationChannel, setNotificationChannel] = useState("");
  const [notificationFrequency, setNotificationFrequency] = useState("");

  const [savedTab, setSavedTab] = useState<TabId | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      const parts = (settings.displayName || "").split(" ");
      setFirstName(settings.firstName || parts[0] || "");
      setLastName(settings.lastName || parts.slice(1).join(" ") || "");
      setJobTitle(settings.jobTitle || "");
      setNotificationChannel(settings.notificationChannel || "");
      setNotificationFrequency(settings.notificationFrequency || "");
    }
  }, [settings]);

  const flashSaved = (which: TabId) => {
    setSavedTab(which);
    setTimeout(() => setSavedTab((prev) => (prev === which ? null : prev)), 2000);
  };

  const handleSaveAccount = async () => {
    const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
    await updateProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      displayName,
      jobTitle,
    });
    updateUser({ displayName });
    flashSaved("account");
  };

  const handleSaveNotifications = async () => {
    await updateProfile({ notificationChannel, notificationFrequency });
    flashSaved("notifications");
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    try {
      await deleteAccount();
      await signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t("settings.danger.error"));
    }
  };

  const TABS: { id: TabId; labelKey: string; Icon: typeof UserIcon }[] = [
    { id: "account", labelKey: "settings.tabs.account", Icon: UserIcon },
    { id: "notifications", labelKey: "settings.tabs.notifications", Icon: Bell },
    { id: "danger", labelKey: "settings.tabs.danger", Icon: AlertTriangle },
  ];

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">{t("settings.title")}</h1>
      <p className="text-sm text-neutral-500 mb-6">{t("settings.subtitle")}</p>

      {/* Tab bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-neutral-200 mb-6">
        {TABS.map(({ id, labelKey, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === id
                ? id === "danger"
                  ? "border-red-500 text-red-600"
                  : "border-steel-600 text-steel-600"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            <Icon className="w-4 h-4" />
            {t(labelKey)}
          </button>
        ))}
      </div>

      {/* Account tab — personal identity */}
      {tab === "account" && (
        <div className="bg-white border border-neutral-200 rounded-card p-6">
          <h2 className="text-lg font-bold text-neutral-900 mb-1">{t("settings.account.title")}</h2>
          <p className="text-sm text-neutral-500 mb-6">{t("settings.account.subtitle")}</p>

          <div className="flex items-center gap-4 mb-6">
            {user?.photoUrl ? (
              <img src={user.photoUrl} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {(user?.displayName || "U").charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-neutral-900">{user?.displayName || "User"}</p>
              <p className="text-xs text-neutral-500">{user?.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-neutral-600 mb-1.5 block">
                  {t("settings.account.firstName")}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 mb-1.5 block">
                  {t("settings.account.lastName")}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-neutral-600 mb-1.5 block">
                {t("settings.account.jobTitle")}
              </label>
              <select
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">{t("settings.selectPlaceholder")}</option>
                {JOB_TITLES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.key)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSaveAccount}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-button bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("settings.save")}
            </button>
            {savedTab === "account" && (
              <span className="text-sm text-green-600 font-medium">{t("settings.saved")}</span>
            )}
          </div>
        </div>
      )}

      {/* Notifications tab */}
      {tab === "notifications" && (
        <div className="bg-white border border-neutral-200 rounded-card p-6">
          <h2 className="text-lg font-bold text-neutral-900 mb-1">
            {t("settings.notifications.title")}
          </h2>
          <p className="text-sm text-neutral-500 mb-6">{t("settings.notifications.subtitle")}</p>

          <div className="space-y-6">
            <div>
              <label className="text-xs font-medium text-neutral-600 mb-2 block">
                {t("settings.notifications.channel")}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {CHANNELS.map(({ id, labelKey, Icon }) => {
                  const selected = notificationChannel === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setNotificationChannel(id)}
                      className={`flex flex-col items-center gap-2 px-3 py-4 rounded-control border text-sm font-medium transition-colors ${
                        selected
                          ? "border-steel-600 bg-steel-600/5 text-steel-700"
                          : "border-neutral-200 text-neutral-600 hover:border-neutral-300"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {t(labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-neutral-600 mb-1.5 block">
                {t("settings.notifications.frequency")}
              </label>
              <select
                value={notificationFrequency}
                onChange={(e) => setNotificationFrequency(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">{t("settings.selectPlaceholder")}</option>
                {FREQUENCIES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.key)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSaveNotifications}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-button bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("settings.save")}
            </button>
            {savedTab === "notifications" && (
              <span className="text-sm text-green-600 font-medium">{t("settings.saved")}</span>
            )}
          </div>
        </div>
      )}

      {/* Danger zone tab */}
      {tab === "danger" && (
        <div className="bg-white border border-red-200 rounded-card p-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-control bg-red-50 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900 mb-1">
                {t("settings.danger.title")}
              </h2>
              <p className="text-sm text-neutral-500 mb-4">{t("settings.danger.subtitle")}</p>
              <ul className="text-sm text-neutral-600 list-disc pl-5 space-y-1 mb-5">
                <li>{t("settings.danger.bullet1")}</li>
                <li>{t("settings.danger.bullet2")}</li>
                <li>{t("settings.danger.bullet3")}</li>
              </ul>
              <button
                onClick={() => {
                  setConfirmText("");
                  setDeleteError(null);
                  setShowDeleteModal(true);
                }}
                className="px-4 py-2 rounded-control bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
              >
                {t("settings.danger.deleteButton")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-card shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-control bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900">
                {t("settings.danger.modalTitle")}
              </h3>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              {t("settings.danger.modalBody", { email: user?.email ?? "" })}
            </p>
            <label className="text-xs font-medium text-neutral-600 mb-1.5 block">
              {t("settings.danger.confirmLabel")}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className={INPUT_CLASS}
              autoFocus
            />
            {deleteError && <p className="text-sm text-red-600 mt-2">{deleteError}</p>}
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-control border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                {t("settings.danger.cancel")}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={confirmText !== "DELETE" || deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-control bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("settings.danger.confirmButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
